import React, { memo, useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { areEqual } from 'Utils/equalityChecks';

import { listCategories, createCategory, updateCategory, deleteCategory } from '../actions';
import type { CostCategory, CostCategoryMapping, PaginationMeta } from '../types';
import { QBO_MAPPABLE_CATEGORIES, COST_CATEGORY_LABELS } from '../types';
import { Pagination } from '../Pagination';
import classes from '../jobCosting.module.css';

const EMPTY_META: PaginationMeta = { currentPage: 1, lastPage: 1, perPage: 50, total: 0 };

interface Props {
  ssoToken: string;
}

interface EditState {
  accountId: string;
  accountName: string;
  itemId: string;
  costCategory: CostCategory;
}

const extractApiError = (err: unknown, fallback: string): string => {
  if (axios.isAxiosError(err)) {
    const data = err.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined;
    if (data?.errors) {
      const first = Object.values(data.errors)[0];
      if (first?.[0]) return first[0];
    }
    if (data?.message) return data.message;
  }
  return fallback;
};

function JobCostingCategories({ ssoToken }: Props) {
  const [mappings, setMappings] = useState<CostCategoryMapping[]>([]);
  const [meta, setMeta] = useState<PaginationMeta>(EMPTY_META);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // New-mapping form
  const [accountId, setAccountId] = useState('');
  const [accountName, setAccountName] = useState('');
  const [itemId, setItemId] = useState('');
  const [costCategory, setCostCategory] = useState<CostCategory>('materials');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Inline edit
  const [editId, setEditId] = useState<string | null>(null);
  const [edit, setEdit] = useState<EditState | null>(null);
  const [editSaving, setEditSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await listCategories(ssoToken, page);
      setMappings(result.data);
      setMeta(result.meta);
    } catch {
      setError('Unable to load category mappings.');
      setMappings([]);
      setMeta(EMPTY_META);
    } finally {
      setLoading(false);
    }
  }, [ssoToken, page]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setFormError(null);

      if (!accountId.trim() && !itemId.trim()) {
        setFormError('Provide a QBO account ID or item ID.');
        return;
      }

      setSaving(true);
      try {
        await createCategory(ssoToken, {
          qboAccountId: accountId.trim() || undefined,
          qboAccountName: accountName.trim() || undefined,
          qboItemId: itemId.trim() || undefined,
          costCategory,
        });
        setAccountId('');
        setAccountName('');
        setItemId('');
        setCostCategory('materials');
        await load();
      } catch (err) {
        setFormError(extractApiError(err, 'Failed to save mapping.'));
      } finally {
        setSaving(false);
      }
    },
    [ssoToken, accountId, accountName, itemId, costCategory, load]
  );

  const handleDelete = useCallback(
    async (id: string) => {
      // eslint-disable-next-line no-alert
      if (!window.confirm('Delete this category mapping?')) return;
      setDeletingId(id);
      setError(null);
      try {
        await deleteCategory(ssoToken, id);
        await load();
      } catch {
        setError('Failed to delete mapping.');
      } finally {
        setDeletingId(null);
      }
    },
    [ssoToken, load]
  );

  const handleEditStart = useCallback((m: CostCategoryMapping) => {
    setError(null);
    setEditId(m.id);
    setEdit({
      accountId: m.qboAccountId || '',
      accountName: m.qboAccountName || '',
      itemId: m.qboItemId || '',
      costCategory: m.costCategory,
    });
  }, []);

  const handleEditCancel = useCallback(() => {
    setEditId(null);
    setEdit(null);
  }, []);

  const handleEditSave = useCallback(async () => {
    if (!editId || !edit) return;
    if (!edit.accountId.trim() && !edit.itemId.trim()) {
      setError('Provide a QBO account ID or item ID.');
      return;
    }
    setEditSaving(true);
    setError(null);
    try {
      await updateCategory(ssoToken, editId, {
        qboAccountId: edit.accountId.trim() || undefined,
        qboAccountName: edit.accountName.trim() || undefined,
        qboItemId: edit.itemId.trim() || undefined,
        costCategory: edit.costCategory,
      });
      setEditId(null);
      setEdit(null);
      await load();
    } catch (err) {
      setError(extractApiError(err, 'Failed to update mapping.'));
    } finally {
      setEditSaving(false);
    }
  }, [ssoToken, editId, edit, load]);

  const renderRow = (m: CostCategoryMapping) => {
    if (editId === m.id && edit) {
      return (
        <tr key={m.id}>
          <td>
            <input
              className={classes.input}
              aria-label="Account name"
              value={edit.accountName}
              onChange={(e) => setEdit({ ...edit, accountName: e.target.value })}
              placeholder="Account name"
            />
          </td>
          <td>
            <input
              className={classes.input}
              aria-label="Account ID"
              value={edit.accountId}
              onChange={(e) => setEdit({ ...edit, accountId: e.target.value })}
              placeholder="Account ID"
            />
          </td>
          <td>
            <input
              className={classes.input}
              aria-label="Item ID"
              value={edit.itemId}
              onChange={(e) => setEdit({ ...edit, itemId: e.target.value })}
              placeholder="Item ID"
            />
          </td>
          <td>
            <select
              className={classes.select}
              aria-label="Cost category"
              value={edit.costCategory}
              onChange={(e) => setEdit({ ...edit, costCategory: e.target.value as CostCategory })}
            >
              {QBO_MAPPABLE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {COST_CATEGORY_LABELS[c]}
                </option>
              ))}
            </select>
          </td>
          <td className={classes.num}>
            <button
              type="button"
              className={classes.iconButton}
              onClick={handleEditSave}
              disabled={editSaving}
            >
              {editSaving ? '…' : 'Save'}
            </button>
            <button type="button" className={classes.iconButton} onClick={handleEditCancel}>
              Cancel
            </button>
          </td>
        </tr>
      );
    }
    return (
      <tr key={m.id}>
        <td>{m.qboAccountName || '—'}</td>
        <td>{m.qboAccountId || '—'}</td>
        <td>{m.qboItemId || '—'}</td>
        <td>{COST_CATEGORY_LABELS[m.costCategory] || m.costCategory}</td>
        <td className={classes.num}>
          <button type="button" className={classes.iconButton} onClick={() => handleEditStart(m)}>
            Edit
          </button>
          <button
            type="button"
            className={classes.iconButton}
            onClick={() => handleDelete(m.id)}
            disabled={deletingId === m.id}
          >
            {deletingId === m.id ? '…' : 'Delete'}
          </button>
        </td>
      </tr>
    );
  };

  const renderList = () => {
    if (loading) {
      return (
        <div className={classes.loadingState}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      );
    }
    if (mappings.length === 0) {
      return (
        <div className={classes.emptyState}>
          No mappings yet. Map a QuickBooks account or item to a cost category above.
        </div>
      );
    }
    return (
      <>
        <div className={classes.tableWrapper}>
          <table className={classes.table}>
            <thead>
              <tr>
                <th>QBO Account</th>
                <th>Account ID</th>
                <th>Item ID</th>
                <th>Category</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>{mappings.map(renderRow)}</tbody>
          </table>
        </div>
        <Pagination meta={meta} onPageChange={setPage} />
      </>
    );
  };

  return (
    <div>
      <form className={classes.inlineForm} onSubmit={handleCreate}>
        <div className={classes.field}>
          <span className={classes.fieldLabel}>QBO Account ID</span>
          <input
            className={classes.input}
            value={accountId}
            onChange={(e) => setAccountId(e.target.value)}
            placeholder="e.g. 82"
          />
        </div>
        <div className={classes.field}>
          <span className={classes.fieldLabel}>Account Name</span>
          <input
            className={classes.input}
            value={accountName}
            onChange={(e) => setAccountName(e.target.value)}
            placeholder="optional"
          />
        </div>
        <div className={classes.field}>
          <span className={classes.fieldLabel}>QBO Item ID</span>
          <input
            className={classes.input}
            value={itemId}
            onChange={(e) => setItemId(e.target.value)}
            placeholder="account or item"
          />
        </div>
        <div className={classes.field}>
          <span className={classes.fieldLabel}>Category</span>
          <select
            className={classes.select}
            value={costCategory}
            onChange={(e) => setCostCategory(e.target.value as CostCategory)}
          >
            {QBO_MAPPABLE_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {COST_CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
        </div>
        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Adding...' : 'Add Mapping'}
        </button>
      </form>

      {formError && <div className={classes.fieldError}>{formError}</div>}
      {error && <div className={`alert alert-danger ${classes.emptyState}`}>{error}</div>}

      {renderList()}
    </div>
  );
}

const JobCostingCategoriesMemo = memo(JobCostingCategories, areEqual);

export { JobCostingCategoriesMemo as JobCostingCategories };
